import { useCustom } from '@refinedev/core';
import { Spin } from 'antd';
import { FlaskConical } from 'lucide-react';
import type { FC } from 'react';

import TokenStatsCards from '@/components/ops/TokenStatsCards';
import UsageDailyChart from '@/components/ops/UsageDailyChart';
import ModelBreakdownChart from '@/components/ops/ModelBreakdownChart';
import QuotaTable from '@/components/ops/QuotaTable';
import EndpointBreakdownList from '@/components/ops/EndpointBreakdownList';

export interface AiUsageStats {
  totalTokens: number;
  totalCost: number;
  byModel: Array<{ model: string; tokens: number; apiCalls: number; cost: number }>;
  topOrganizations: Array<{ id: string; name: string; tokens: number; uniqueUsers?: number }>;
  topEndpoints: Array<{ endpoint: string; tokens: number; calls: number }>;
  dailyUsage: Array<{ date: string; tokens: number }>;
}

const TokenWatcherPage: FC = () => {
  const { data: statsData, isLoading, isError } = useCustom<{ data: AiUsageStats }>({
    url: '/api/v1/admin/stats/ai-usage',
    method: 'get',
    config: { query: { period: '30d' } },
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <GradientHeader />
        <div className="fc-card border-red-300 p-6 dark:border-red-800">
          <p className="font-medium text-red-600 dark:text-red-400">
            Impossible de charger les statistiques de consommation IA.
          </p>
        </div>
      </div>
    );
  }

  const stats = statsData?.data?.data;

  return (
    <div className="space-y-6">
      <GradientHeader />
      <TokenStatsCards stats={stats} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <UsageDailyChart dailyUsage={stats?.dailyUsage} />
        </div>
        <ModelBreakdownChart byModel={stats?.byModel} />
      </div>

      <QuotaTable />
      <EndpointBreakdownList topEndpoints={stats?.topEndpoints} />
    </div>
  );
};

const GradientHeader: FC = () => (
  <div className="fc-gradient-header rounded-2xl p-6 text-white">
    <div className="flex items-center gap-3">
      <FlaskConical className="h-6 w-6" />
      <div>
        <h1 className="text-xl font-bold">Token Watcher</h1>
        <p className="mt-1 text-sm text-blue-100">Consommation IA en temps réel</p>
      </div>
    </div>
  </div>
);

export default TokenWatcherPage;
